import type { MergedRewardData, RewardOverviewData } from '@/schemas/v2/reward'
import type { TFunction } from 'i18next'

type AcquisitionStatus = NonNullable<MergedRewardData['acquisitionStatus']>

/**
 * SELECT_ONE（groupType='SINGLE'）グループで「1つ分の選択を使い切った」ことを表すステータス。
 * バックエンドの SELECT_ONE ブロック判定（SelectOneGroupBlocking）と対応する集合で、
 * この状態のメンバーが1件でもあれば、残りが UNOBTAINED / UNAVAILABLE で返っていてもグループとしては終端。
 * 配列順がそのまま代表ステータスの優先順になる（達成系 → 完了系 → ハズレ）。
 *
 * LOST は非 repeatable のハズレ確定を想定している。repeatable + consumptionRequired の特典は
 * ハズレ後もスタンプを貯め直せば再挑戦できるが、その場合は再挑戦可能になった時点で
 * ACHIEVABLE が返り、上位の分岐が先に効くため一時的な表示に留まる（#3405）。
 */
const SINGLE_GROUP_OBTAINED_PRIORITY = [
  'ACHIEVED',
  'UNAPPLIED',
  'EXCHANGED',
  'APPLIED',
  'USED',
  'LOST',
] as const satisfies readonly AcquisitionStatus[]

/**
 * グループメンバーの獲得状況を代表値に集約する。
 * 優先順: ACHIEVABLE > (SINGLE のみ: 獲得済み/終端ステータス) > ACHIEVED > UNOBTAINED
 *         > (EXCHANGED/USED/UNAPPLIED/APPLIED/LOST) > UNAVAILABLE/ENDED/SOLD_OUT
 * 全メンバーが UNAVAILABLE/ENDED/SOLD_OUT の場合のみ、それらを代表値として返す。
 * （認証済みの場合のみ設定される。未認証時は全 null のため undefined を返す）
 */
function resolveGroupAcquisitionStatus(members: MergedRewardData[]): AcquisitionStatus | undefined {
  const statuses = members.map((m) => m.acquisitionStatus).filter((s): s is AcquisitionStatus => s != null)
  if (statuses.length === 0) return undefined
  // 「まだ獲得できる」は常に最優先。repeatable 特典は獲得後も ACHIEVABLE が返るため、
  // SINGLE の獲得済み判定より前に評価する必要がある。
  if (statuses.includes('ACHIEVABLE')) return 'ACHIEVABLE'
  // SELECT_ONE: 1件獲得（またはハズレ確定）すると残りは獲得不可になる。
  // #3495 のバックエンドリリース後は UNAVAILABLE、それ以前（フロント先行リリース中）は
  // UNOBTAINED で返る（#2929 / #3405）ため、どちらでも獲得済みメンバーのステータスを
  // 優先してグループ代表とする。この分岐は移行期間の互換性として必ず残すこと。
  // MULTI は未獲得の特典が実際に残るため、従来どおり UNOBTAINED を優先する。
  if (members.some((m) => m.groupType === 'SINGLE')) {
    const obtained = SINGLE_GROUP_OBTAINED_PRIORITY.find((s) => statuses.includes(s))
    if (obtained !== undefined) return obtained
  }
  if (statuses.includes('ACHIEVED')) return 'ACHIEVED'
  if (statuses.includes('UNOBTAINED')) return 'UNOBTAINED'
  // UNAVAILABLE / ENDED / SOLD_OUT は「そのメンバーはもう獲得できない」ことしか意味せず、
  // グループ全体の到達状態を代表しない。獲得可能・獲得済みのメンバー
  // （EXCHANGED/USED/UNAPPLIED/APPLIED/LOST）が 1 件でもあればそちらを代表にする。
  // UNAVAILABLE を明示的に除外しないと、メンバー配列の並び順によって代表値が変わってしまう（#3495）。
  const activeStatuses = statuses.filter((s) => s !== 'ENDED' && s !== 'SOLD_OUT' && s !== 'UNAVAILABLE')
  if (activeStatuses.length > 0) return activeStatuses[0]
  // 全メンバーが獲得不可の場合のみ UNAVAILABLE を代表にする。
  // ブロック元の特典が非公開等で一覧に含まれないケースが該当する（#3495）。
  if (statuses.includes('UNAVAILABLE')) return 'UNAVAILABLE'
  return statuses[0]
}

/**
 * MergedRewardData[] を RewardOverviewData[] に変換する。
 * - groupType === 'NONE': そのまま個別エントリ
 * - groupType !== 'NONE': 同じ groupId を持つものを1エントリにまとめる
 *   - name: グループ名 (groupName)、null の場合は代表特典名にフォールバック
 *   - label: "N件の特典"
 *   - id / image: グループ先頭特典（APIレスポンス順に依存）
 *   - acquisitionStatus: resolveGroupAcquisitionStatus の結果
 */
export function buildDisplayRewards(t: TFunction, rewards: MergedRewardData[]): RewardOverviewData[] {
  const groupMembersMap = new Map<number, MergedRewardData[]>()
  for (const r of rewards) {
    if (r.groupType !== 'NONE' && r.groupId !== null) {
      const members = groupMembersMap.get(r.groupId) ?? []
      members.push(r)
      groupMembersMap.set(r.groupId, members)
    }
  }

  const result: RewardOverviewData[] = []
  const seenGroupIds = new Set<number>()

  for (const r of rewards) {
    if (r.groupType === 'NONE' || r.groupId === null) {
      result.push({
        id: r.id,
        name: r.name,
        image: r.image,
        label: r.label,
        acquisitionStatus: r.acquisitionStatus ?? undefined,
        conditions: r.conditions,
        conditionGroups: r.conditionGroups,
      })
    } else {
      if (!seenGroupIds.has(r.groupId)) {
        seenGroupIds.add(r.groupId)
        const groupMembers = groupMembersMap.get(r.groupId) ?? [r]
        result.push({
          id: r.id,
          name: r.groupName ?? r.name,
          image: r.image,
          label: t('reward.group.item_count', '{{count}}件の特典', { count: groupMembers.length }),
          acquisitionStatus: resolveGroupAcquisitionStatus(groupMembers),
          // conditions と conditionGroups は必ず同じ代表特典（先頭メンバー）から取る。
          // 別メンバーのものと混ぜると conditions[].groupId の対応が壊れる（#3581）。
          conditions: groupMembers[0]?.conditions ?? [],
          conditionGroups: groupMembers[0]?.conditionGroups ?? [],
        })
      }
    }
  }
  return result
}
